import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Body, {
  Size as BodySize,
  LetterCase as BodyLetterCase,
  Spacing as BodySpacing,
} from 'components/Typography/Body';
import Spinner from 'components/Common/Spinner';
import Modal from 'components/Common/Modal';
import Confirmation from 'components/Common/Confirmation';
import Toast from 'components/Common/Toast';
import BrandSummary from 'components/Pages/Brands/BrandSummary';
import {
  selectAcceptBrandState,
  selectFetchRejectedBrandsState,
} from 'state/selectors/brands';
import { selectAuthenticatedUserState } from 'state/selectors/auth';
import {
  acceptBrand,
  clearBrandsState,
  fetchRejectedBrands,
} from 'state/actions/brands';
import useModal from 'hooks/useModal';
import ModalType from 'enums/modal/modalType.enum';

import classes from './RejectedBrands.module.scss';

const RejectedBrands = () => {
  const dispatch = useDispatch();

  const { user } = useSelector(selectAuthenticatedUserState, shallowEqual);

  const {
    error: errorRejectedBrands,
    success: successRejectedBrands,
    loading: loadingRejectedBrands,
    rejectedBrands,
  } = useSelector(selectFetchRejectedBrandsState, shallowEqual);

  const {
    error: errorAcceptBrand,
    success: successAcceptBrand,
    loading: loadingAcceptBrand,
  } = useSelector(selectAcceptBrandState, shallowEqual);

  const [brandsToShow, setBrandsToShow] = useState([]);
  const [brandIdToDelete, setBrandIdToDelete] = useState(null);

  const { modal, onOpenModalHandler, onCloseModalHandler } = useModal();

  useEffect(() => {
    if (user) dispatch(fetchRejectedBrands(user.network));

    return () => {
      dispatch(clearBrandsState());
    };
  }, [user]);

  useEffect(() => {
    if (successRejectedBrands) {
      setBrandsToShow(rejectedBrands);
      dispatch(clearBrandsState());
    }
  }, [successRejectedBrands, rejectedBrands]);

  useEffect(() => {
    if (successAcceptBrand) {
      onCloseModalHandler();
      setBrandsToShow((prevState) =>
        prevState.filter((doc) => doc.uid !== brandIdToDelete)
      );
      setBrandIdToDelete(null);
    }
  }, [successAcceptBrand, brandIdToDelete]);

  const onClickAcceptHandler = useCallback((brand) => {
    onOpenModalHandler(ModalType.ACCEPT_BRAND, {
      brandId: brand.uid,
    });
  }, []);

  const onAcceptBrandHandler = useCallback(() => {
    setBrandIdToDelete(modal.brandId);
    dispatch(acceptBrand({ brandId: modal.brandId }));
  }, [modal]);

  return (
    <>
      {errorRejectedBrands && (
        <Toast text={errorRejectedBrands} id="Fetch rejected brands error" />
      )}
      {errorAcceptBrand && (
        <Toast text={errorAcceptBrand} id="Accept brand error" />
      )}
      <Modal
        isOpen={modal.type === ModalType.ACCEPT_BRAND}
        onClose={onCloseModalHandler}
      >
        <Confirmation
          title="Accept brand"
          description="Are you sure you want to accept the selected brand?"
          onAccept={onAcceptBrandHandler}
          onCancel={onCloseModalHandler}
          loading={loadingAcceptBrand}
        />
      </Modal>
      <div className={classes.container}>
        <div className={classes.content}>
          <Body
            letterCase={BodyLetterCase.Uppercase}
            spacing={BodySpacing.M}
            className={classes.title}
          >
            Brands rejected
          </Body>
          {loadingRejectedBrands ? (
            <Spinner className={classes.spinner} />
          ) : (
            <>
              {brandsToShow.length === 0 ? (
                <Body
                  size={BodySize.XS}
                  spacing={BodySpacing.S}
                  className={classes.noBrands}
                >
                  There are no brands rejected.
                </Body>
              ) : (
                <>
                  <div className={classes.brands}>
                    {brandsToShow.map((brand, index) => {
                      const key = `brand-${index}`;
                      return (
                        <BrandSummary
                          key={key}
                          brand={brand}
                          onAccept={() => onClickAcceptHandler(brand)}
                          isAccepted={false}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RejectedBrands;
